import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { JobCatalogNodeData, PointNode } from 'modules/job_catalog/pages/JobCatalogTreePage/types'
import type useZoom from 'modules/people_chart/hooks/useZoom'

interface Props {
  zoomToNodeId?: string
  zoom: ReturnType<typeof useZoom>
  tree: PointNode<JobCatalogNodeData>[]
  nodeData: JobCatalogNodeData[]
  uncollapsedIds: Set<string>
  onCollapseChange?: (nodeId: string) => void
  setSelectedNodeId: (id: string | null) => void
  zoomToNode: (node: PointNode<JobCatalogNodeData>) => void
}

export const useZoomToNode = ({
  zoomToNodeId,
  zoom,
  tree,
  nodeData,
  uncollapsedIds,
  onCollapseChange,
  setSelectedNodeId,
  zoomToNode,
}: Props) => {
  useUnsafeEffect(() => {
    if (!zoomToNodeId || !zoom.isReady || tree.length === 0) return

    const nodeExists = nodeData.find((node) => node.id === zoomToNodeId)
    if (!nodeExists) return

    const getParentNodesToExpand = (nodeId: string) => {
      const targetNode = nodeData.find((node) => node.id === nodeId)
      if (!targetNode) return []

      const parents = []
      let currentParentId = targetNode.parentId

      while (currentParentId) {
        parents.push(currentParentId)
        const parentNode = nodeData.find((node) => node.id === currentParentId)
        currentParentId = parentNode?.parentId || null
      }

      return parents
    }

    const parentsToExpand = getParentNodesToExpand(zoomToNodeId)
    const needsExpansion = parentsToExpand.some((parentId) => !uncollapsedIds.has(parentId))

    parentsToExpand.forEach((parentId) => {
      if (!uncollapsedIds.has(parentId)) {
        onCollapseChange?.(parentId)
      }
    })

    const findAndZoomToNode = () => {
      const targetNode = tree.find(
        (node) => node.id === zoomToNodeId || node.data.id === zoomToNodeId
      )
      if (targetNode) {
        setSelectedNodeId(targetNode.id || null)
        zoomToNode(targetNode)
        return true
      }
      return false
    }

    const delay = needsExpansion ? 200 : 50

    let retryTimeout: NodeJS.Timeout | null = null

    const timeout = setTimeout(() => {
      if (!findAndZoomToNode()) {
        retryTimeout = setTimeout(findAndZoomToNode, 300)
      }
    }, delay)

    return () => {
      clearTimeout(timeout)
      if (retryTimeout) {
        clearTimeout(retryTimeout)
      }
    }
  }, [zoomToNodeId, zoom.isReady, tree, nodeData, uncollapsedIds])
}
